import React, { useState } from 'react';
import { X, Loader2, Gauge, Calendar, User, AlignLeft } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { Modal } from '../common';
import toast from 'react-hot-toast';

const todayStr = () => new Date().toISOString().slice(0, 10);

const RegistrarKmModal = ({ isOpen, onClose, onSuccess, vehiculo }) => {
  const [fecha, setFecha] = useState(todayStr());
  const [kmDia, setKmDia] = useState('');
  const [usuario, setUsuario] = useState('');
  const [observacion, setObservacion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetForm = () => {
    setFecha(todayStr());
    setKmDia('');
    setUsuario('');
    setObservacion('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fecha) {
      setError('La fecha es obligatoria');
      return;
    }
    if (kmDia === '' || Number(kmDia) < 0) {
      setError('Ingresa el kilometraje recorrido hoy');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axiosInstance.post(`/api/equipos-moviles/vehiculos/${vehiculo.id}/km`, {
        fecha,
        km_dia: Number(kmDia),
        usuario: usuario.trim(),
        observacion: observacion.trim(),
      });
      toast.success('Kilometraje actualizado');
      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error registrando km:', err);
      setError(err.response?.data?.error || 'Error al registrar el kilometraje');
    } finally {
      setLoading(false);
    }
  };

  if (!vehiculo) return null;

  const inputClass = "w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition-all";
  const labelClass = "flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5";

  const kmFinalEstimado = Number(vehiculo.kilometraje_actual || 0) + (Number(kmDia) || 0);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header onClose={handleClose}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-sura-blue to-sura-blue-light rounded-xl text-white shadow-lg shadow-sura-blue/20">
            <Gauge size={20} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Registrar Kilometraje</h3>
            <p className="text-xs text-gray-500 font-normal">{vehiculo.nombre}</p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit} id="registrar-km-form" className="space-y-4">
          <div className="flex items-center justify-between px-4 py-3 bg-blue-50 rounded-xl border border-blue-100">
            <span className="text-sm text-gray-600">Kilometraje actual</span>
            <span className="text-lg font-bold text-gray-900 font-mono">{Number(vehiculo.kilometraje_actual).toLocaleString('es-VE')} km</span>
          </div>

          <div>
            <label className={labelClass}><Calendar size={13} /> Fecha</label>
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className={inputClass} />
          </div>

          <div>
            <label className={labelClass}><Gauge size={13} /> Kilómetros recorridos hoy</label>
            <input
              type="number"
              min="0"
              step="0.1"
              value={kmDia}
              onChange={(e) => setKmDia(e.target.value)}
              placeholder="Ej: 30"
              className={inputClass}
              autoFocus
            />
            {kmDia !== '' && !isNaN(Number(kmDia)) && (
              <p className="text-xs text-gray-400 mt-1.5">
                Nuevo total: <span className="font-semibold text-gray-600">{kmFinalEstimado.toLocaleString('es-VE')} km</span>
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}><User size={13} /> Conductor / Usuario</label>
            <input
              type="text"
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              placeholder="Opcional"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}><AlignLeft size={13} /> Observación / Avería reportada</label>
            <textarea
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              placeholder="Sin novedad..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm animate-fadeIn">
              <X size={16} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </form>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-end w-full gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="registrar-km-form"
            disabled={loading}
            className="px-6 py-2.5 font-semibold rounded-xl transition-all flex items-center gap-2 shadow-sm bg-gradient-to-r from-sura-blue to-sura-blue-light text-white hover:from-sura-blue-dark hover:to-sura-blue shadow-sura-blue/20 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default RegistrarKmModal;
